import { FC, useMemo } from 'react'
import { useSelectTankStore } from '../../store/selectTank/useSelectTankStore'
import { useGroupStore } from '../../store/group/useGroupStore'
import { useTankStore } from '../../store/tank/useTankStore'
import { useBatchStore } from '../../store/batch/useBatchStore'
import { useSchoolStore } from '../../store/school/useSchoolStore'
import { SubmitTankType } from '../../types/apiResponse/tankTypes'
import TankChip from './TankChip'
import type { School } from '@/types/domain'

export type TankChipContainerProps = {
  title: string
  groupId: string
  instantSelect?: boolean
  onInstantSelect?: (tank: SubmitTankType) => void
  availableMap?: { [tankId: string]: string[] }
  singleSelect?: boolean
}

export const TankChipContainer: FC<TankChipContainerProps> = ({
  title,
  groupId,
  instantSelect = false,
  onInstantSelect,
  availableMap,
  singleSelect = false,
}) => {
  const { attach, detach, contains, close } = useSelectTankStore()
  const getTankSchoolMappingsByGroupId = useGroupStore((state) => state.getTankSchoolMappingsByGroupId)
  const getGroupedTankIds = useGroupStore((state) => state.getGroupedTankIds)
  const groupsWithDetails = useGroupStore((state) => state.groupsWithDetails)
  const tanks = useTankStore((state) => state.tanks)
  const schools = useSchoolStore((state) => state.schools)
  const batchName = useBatchStore((state) => state.batches[groupId]?.batchName ?? '')

  // Helper: Find school info from groupsWithDetails for validation
  const getSchoolInfoFromGroups = (schoolId: string): School | undefined => {
    for (const group of groupsWithDetails) {
      const item = group.items?.find((item) => item.school.id === schoolId)
      if (item) return item.school
    }
    return undefined
  }

  // Get tank mappings based on groupId type
  const tankMappings = useMemo(() => {
    if (groupId === '$empty') {
      // For $empty group: get tanks without schoolId (empty tanks)
      const groupedTankIds = getGroupedTankIds()
      return Object.keys(tanks)
        .filter((tankId) => !groupedTankIds.includes(tankId) && !tanks[tankId].schoolId)
        .map((tankId) => ({ tankId, schoolId: '' }))
    } else if (groupId === '$ungrouped') {
      // For $ungrouped group: get tanks with schoolId but not in any group
      const groupedTankIds = getGroupedTankIds()
      return Object.keys(tanks)
        .filter((tankId) => !groupedTankIds.includes(tankId) && tanks[tankId].schoolId)
        .map((tankId) => ({ tankId, schoolId: tanks[tankId].schoolId || '' }))
    } else if (groupId === '$ungrouped-zero-count') {
      // For $ungrouped-zero-count: get ungrouped tanks with schoolId where count=0
      const groupedTankIds = getGroupedTankIds()
      return Object.keys(tanks)
        .filter((tankId) => {
          if (groupedTankIds.includes(tankId)) return false
          const tank = tanks[tankId]
          if (!tank.schoolId) return false
          const school = schools[tank.schoolId]
          return school && school.count === 0
        })
        .map((tankId) => ({ tankId, schoolId: tanks[tankId].schoolId || '' }))
    } else if (groupId === '$stocked') {
      // For $stocked: all tanks with fish (count > 0), regardless of group membership
      return Object.keys(tanks)
        .filter((tankId) => {
          const tank = tanks[tankId]
          if (!tank.schoolId) return false
          const school = schools[tank.schoolId]
          return school && school.count != null && school.count > 0
        })
        .map((tankId) => ({ tankId, schoolId: tanks[tankId].schoolId || '' }))
    } else {
      // For regular groups: use existing mapping
      return getTankSchoolMappingsByGroupId(groupId)
    }
  }, [groupId, tanks, schools, getTankSchoolMappingsByGroupId, getGroupedTankIds])

  // Sort tank mappings by tank name
  const sortedTankMappings = useMemo(() => {
    return [...tankMappings].sort((a, b) => {
      const nameA = tanks[a.tankId]?.name?.toLowerCase() || ''
      const nameB = tanks[b.tankId]?.name?.toLowerCase() || ''
      return nameA.localeCompare(nameB)
    })
  }, [tankMappings, tanks])

  const tankChips =
    sortedTankMappings.length === 0
      ? []
      : sortedTankMappings.map((mapping, index) => {
          const tankId = mapping.tankId
          const schoolId = mapping.schoolId || null

          // Get school dates for validation
          // Priority: groupsWithDetails > schools store > null
          let schoolStartDate: Date | null = null
          let schoolEndDate: Date | null = null

          if (schoolId) {
            // Try to get from groupsWithDetails first (has endDate)
            const schoolFromGroups = getSchoolInfoFromGroups(schoolId)
            if (schoolFromGroups) {
              schoolStartDate = schoolFromGroups.startDate
              schoolEndDate = schoolFromGroups.endDate ?? null
            } else {
              // Fallback to schools store (for ungrouped tanks)
              const schoolFromStore = schools[schoolId]
              if (schoolFromStore?.startDate) {
                schoolStartDate =
                  typeof schoolFromStore.startDate === 'string'
                    ? new Date(schoolFromStore.startDate)
                    : schoolFromStore.startDate
              }
              // endDate not available in SchoolStore, defaults to null (ongoing)
            }
          }

          const tankType: SubmitTankType = {
            tankId: tankId,
            tankName: tanks[tankId].name,
            batchId: groupId,
            batchName: batchName,
            // School info for validation
            schoolId: schoolId,
            schoolStartDate: schoolStartDate,
            schoolEndDate: schoolEndDate,
          }

          const disabled = !!availableMap && (!availableMap[tankId] || availableMap[tankId].length === 0)
          const onTankChipClick = (tankId: string) => {
            if (disabled) return
            if (instantSelect && onInstantSelect) {
              onInstantSelect(tankType)
            } else {
              if (contains(tankId)) {
                detach(tankId)
              } else {
                if (singleSelect) {
                  close()
                }
                attach(tankType)
              }
            }
          }
          return (
            <TankChip
              key={index}
              submitTank={tankType}
              isActivated={contains(tankId)}
              onTankChipClick={onTankChipClick}
              disabled={disabled}
            />
          )
        })

  return (
    <>
      {tankChips.length === 0 ? (
        <div className="w-full py-4 text-center text-tdpldarkgrey text-sm">
          <p>No tanks available.</p>
        </div>
      ) : (
        <div
          className="overflow-x-hidden w-full max-w-full"
          data-testid={`tank-chip-container-${groupId}`}
          data-group-id={groupId}
          data-group-name={batchName}
          data-tank-count={tankChips.length}
        >
          <p
            className="text-tdplindigo text-[1rem] my-2 overflow-x-hidden"
            data-testid={`tank-container-title-${groupId}`}
          >
            {title}
          </p>
          <div
            className="flex flex-row flex-wrap my-2 mx-1 overflow-x-hidden"
            data-testid={`tank-chips-list-${groupId}`}
            role="group"
            aria-label={`Tank selection for ${title}`}
          >
            {tankChips}
          </div>
        </div>
      )}
    </>
  )
}
